import type { ErrorOption, FieldValues, UseFormSetError } from "react-hook-form";
import { useIntl } from "react-intl";

import type { IIncorrectInputDataError } from "types/formErrors";

export const useFormErrors = () => {
    const { formatMessage } = useIntl();

    const errorMessages = {
        EmptyField: formatMessage({ id: "errors.invalid_type_received_null" }),
        WrongPhoneFormat: formatMessage({ id: "errors.invalid_string.regex" }),
        WrongEmailFormat: formatMessage({ id: "errors.invalid_string.email" }),
        FileNotExists: formatMessage({ id: "errors.file_not_exists" }),
        FileContentDoesntMatchExtension: formatMessage({
            id: "errors.file_content_doesnt_match_extension",
        }),
        FileExtensionNotAllowed: formatMessage({
            id: "errors.file_extension_not_allowed",
        }),
        FileTooLarge: formatMessage({
            id: "errors.file_too_large",
        }),
    };

    const findKeyWithString = (obj: Record<string, unknown>, searchString: string): string | null =>
        Object.keys(obj).find((key) => searchString.includes(key)) || null;

    const mapCodeErrorToFormError = (error: IIncorrectInputDataError): ErrorOption => {
        const message = errorMessages[error.code] || formatMessage({ id: "errors.custom" });

        return {
            type: error.code,
            message,
        };
    };

    const displayError = ({
        e,
        setError,
        obj,
    }: {
        obj: Record<string, unknown>;
        setError: UseFormSetError<FieldValues>;
        e: IIncorrectInputDataError;
    }) => {
        const { fieldName } = e;
        const mappedKey = findKeyWithString(obj, fieldName);

        setError(mappedKey ?? fieldName, mapCodeErrorToFormError(e));
    };

    const handleFileUploadError = ({
        error,
        setError,
        name,
        maxSizeInMB,
        maxSizeInMBForMp4,
    }: {
        error: unknown;
        setError: UseFormSetError<FieldValues>;
        name: string;
        maxSizeInMB: number;
        maxSizeInMBForMp4?: string;
    }): void => {
        const errorCause = error as {
            status?: number;
            statusText?: string;
            url?: string;
            data?: unknown;
            headers?: Record<string, unknown>;
        };

        if (errorCause.data && typeof errorCause.data === "object" && "File" in errorCause.data) {
            const errorData = errorCause.data as { File?: string };
            if (errorData.File?.includes("Too big file")) {
                const message = maxSizeInMBForMp4
                    ? formatMessage({ id: "fileSizeMp4" }, { size: maxSizeInMBForMp4 })
                    : formatMessage({ id: "fileSize" }, { size: maxSizeInMB });
                setError(name, {
                    type: "invalidFileSize",
                    message,
                });
                return;
            }
        }

        setError(name, {
            type: "uploadFailed",
            message: formatMessage({ id: "fileUploadError" }),
        });
    };

    return { mapCodeErrorToFormError, displayError, handleFileUploadError };
};
